/**
 * What the capsule screens (detail, contents, compare) say when reading a
 * capsule fails — in this app's own words, by error code.
 *
 * The server's own error text is never shown: it can name the storage service
 * and print the capsule's full storage reference (bucket path). Only the
 * browser-made network messages (connection.ts, status 0) are passed through;
 * they name nothing but the server the user chose. An unknown code gets a
 * general message.
 *
 * Pure module so it can be unit-tested with `node --test`.
 */

type ErrorLike = { code?: string; message?: string; status?: number } | null | undefined;

/** The server no longer has this capsule's content (or cannot locate it from the record). */
export const CONTENT_UNAVAILABLE_CODES: readonly string[] = ['BLOB_GONE', 'BAD_STORAGE_REF'];

const MESSAGES: Record<string, string> = {
  BLOB_GONE:
    "This server no longer has this capsule's content. The record is intact; send the capsule again (from the CLI or the Send screen) to store its content.",
  BAD_STORAGE_REF: "This capsule's record does not point to content this server can read.",
  BLOB_READ_FAILED: "The server could not read this capsule's content. Try again later.",
  STREAM_ERROR: "The server could not read this capsule's content. Try again later.",
  NOT_FOUND: 'This capsule was not found.',
  BAD_ID: 'This capsule was not found.',
  ODOO_NOT_CONFIGURED: 'The Collect server cannot read capsules right now. Try again later.',
  UNAVAILABLE: 'The Collect server did not answer. Try again in a minute.',
  DECOMPRESSION_FAILED: 'The content could not be decompressed in the browser.',
  HASH_MISMATCH: 'The content does not match its SHA-256 hash: it may have been changed or damaged.',
  JSON_PARSE_FAILED: 'The content was decompressed, but it is not a readable capsule bundle.',
};

const BROWSER_NETWORK_CODES = new Set(['NETWORK', 'CORS_BLOCKED']);
const CODE_RE = /^[A-Z][A-Z0-9_]{1,40}$/;

export function isContentUnavailable(e: ErrorLike): boolean {
  return Boolean(e?.code && CONTENT_UNAVAILABLE_CODES.includes(e.code));
}

export function capsuleErrorMessage(e: ErrorLike): string {
  const code = typeof e?.code === 'string' ? e.code : '';
  if (BROWSER_NETWORK_CODES.has(code) && e?.status === 0 && typeof e?.message === 'string' && e.message) {
    return e.message;
  }
  if (code === 'INVALID_API_KEY' || code === 'UNAUTHORIZED' || e?.status === 401 || e?.status === 403) {
    return 'The access key was not accepted. Check it under Settings.';
  }
  if (MESSAGES[code]) return MESSAGES[code];
  return `Something went wrong while reading this capsule${CODE_RE.test(code) ? ` (${code})` : ''}. Try again later.`;
}
